<template>
  <!-- Premium User View -->
  <div v-if="isPaidUser && !isAdmin" class="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-12 px-4">
    <div class="max-w-6xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-12">
        <div class="mb-6 flex justify-center">
          <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <svg class="w-12 h-12 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
        <h1 class="text-4xl font-bold text-gray-900 mb-4">You're Already Premium! 🎉</h1>
        <p class="text-lg text-gray-600 mb-8">
          You have full access to all AI-powered features. Here's what you can do:
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link
            to="/dashboard"
            class="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold text-lg hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl"
          >
            Go to Dashboard
          </router-link>
          <router-link
            to="/settings?tab=subscription"
            class="px-8 py-4 bg-white border-2 border-amber-600 text-amber-600 rounded-xl font-bold text-lg hover:bg-amber-50 transition-all shadow-md hover:shadow-lg"
          >
            Manage Subscription
          </router-link>
        </div>
      </div>

      <!-- Premium Features Grid -->
      <div class="bg-white rounded-2xl shadow-xl p-8 md:p-12">
        <h2 class="text-2xl font-bold text-gray-900 mb-8 text-center">Your Premium Benefits</h2>
        
        <div class="grid md:grid-cols-2 gap-8">
          <!-- Main Features -->
          <div class="space-y-6">
            <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
              <span class="text-2xl">⭐</span> Everything in Free, plus AI Features:
            </h3>
            
            <div class="space-y-4">
              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">150 API Calls/Day</p>
                  <p class="text-sm text-gray-600">Use in ChatGPT & Claude Desktop via MCP integration</p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">RL Portfolio Optimization</p>
                  <p class="text-sm text-gray-600">Deep reinforcement learning for smarter trades</p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">AI Consensus Analysis</p>
                  <p class="text-sm text-gray-600">Multi-engine agreement for confident decisions</p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">Market Sentiment & Trends</p>
                  <p class="text-sm text-gray-600">Real-time market mood tracking with FinBERT</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Features -->
          <div class="space-y-6">
            <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
              <span class="text-2xl">🚀</span> Advanced Analytics:
            </h3>
            
            <div class="space-y-4">
              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">Predictive Analytics <span class="text-purple-600 text-xs">61.18% validated</span></p>
                  <p class="text-sm text-gray-600">LSTM-powered stock predictions with proven accuracy</p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">AI Financial Assistant</p>
                  <p class="text-sm text-gray-600">Unlimited intelligent chat for market insights</p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">Portfolio Analysis</p>
                  <p class="text-sm text-gray-600">Comprehensive risk decomposition analysis</p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">Stock Discovery AI</p>
                  <p class="text-sm text-gray-600">Find hidden opportunities before the market</p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                <div>
                  <p class="font-semibold text-gray-900">Batch Predictions</p>
                  <p class="text-sm text-gray-600">Analyze multiple stocks simultaneously</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- CTA Bottom -->
        <div class="mt-12 pt-8 border-t border-gray-200 text-center">
          <p class="text-gray-600 mb-4">Ready to make smarter investment decisions?</p>
          <router-link
            to="/dashboard"
            class="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
          >
            <span>Start Using Premium Features</span>
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="max-w-7xl mx-auto space-y-12 pb-12">
    <!-- Page Header -->
    <div class="text-center pt-8">
      <div class="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        <span>7-Day Free Trial Included</span>
      </div>
      <h1 class="text-5xl font-bold text-gray-900 mb-4">
        Unlock AI-Powered <span class="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">Investment Intelligence</span>
      </h1>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Transform your portfolio with cutting-edge machine learning algorithms trusted by thousands of investors
      </p>
      <div class="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
        <svg class="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <span><strong>New:</strong> Use in ChatGPT & Claude Desktop via API/MCP integration</span>
        <router-link to="/mcp" class="text-purple-600 hover:text-purple-800 underline ml-1">Learn more →</router-link>
      </div>
    </div>

    <!-- Billing Toggle -->
    <div class="flex items-center justify-center gap-4">
      <span class="text-gray-700 font-medium" :class="{ 'text-gray-400': billingCycle === 'yearly' }">Monthly</span>
      <button
        @click="toggleBillingCycle"
        class="relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
        :class="billingCycle === 'yearly' ? 'bg-amber-500' : 'bg-gray-300'"
      >
        <span
          class="inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform"
          :class="billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'"
        />
      </button>
      <span class="text-gray-700 font-medium" :class="{ 'text-gray-400': billingCycle === 'monthly' }">
        Yearly
        <span class="ml-2 text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-full">Save 15%</span>
      </span>
    </div>

    <!-- Pricing Cards -->
    <div class="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto px-4">
      <!-- Free Plan -->
      <div class="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 hover:shadow-xl transition-shadow">
        <div class="mb-6">
          <h3 class="text-2xl font-bold text-gray-900 mb-2">Free Plan</h3>
          <p class="text-gray-600 text-sm mb-4">Perfect for getting started</p>
          <div class="flex items-baseline gap-2">
            <span class="text-5xl font-bold text-gray-900">$0</span>
            <span class="text-gray-600">/month</span>
          </div>
        </div>

        <div class="border-t border-gray-200 pt-6 mb-8">
          <ul class="space-y-4">
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-gray-700">Portfolio tracking & management</span>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-gray-700">Investment goals & risk tolerance</span>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-gray-700">Real-time stock quotes</span>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-gray-700">Basic portfolio analytics</span>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-gray-700">Watchlist & price alerts</span>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span class="text-gray-700">Stock search & research</span>
            </li>
          </ul>
        </div>

        <button 
          disabled
          class="w-full py-4 px-6 bg-gray-200 text-gray-500 rounded-xl font-semibold cursor-not-allowed text-lg"
        >
          Current Plan
        </button>
      </div>

      <!-- Premium Plan -->
      <div class="bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 rounded-2xl shadow-2xl border-2 border-amber-400 p-8 relative transform hover:scale-105 transition-transform">
        <!-- Popular Badge -->
        <div class="absolute -top-5 left-1/2 transform -translate-x-1/2">
          <div class="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span>RECOMMENDED</span>
          </div>
        </div>

        <div class="mb-6 pt-2">
          <div class="flex items-center gap-3 mb-3">
            <h3 class="text-2xl font-bold text-gray-900">Premium Plan</h3>
            <div class="inline-flex items-center gap-1.5 bg-purple-100 text-purple-800 px-2.5 py-1 rounded-lg text-xs font-bold border border-purple-300">
              <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span>61.18% Validated</span>
            </div>
          </div>
          <p class="text-gray-700 text-sm mb-4">Unlock all AI-powered features with independently verified accuracy</p>
          
          <!-- Q1 Launch Deal Banner -->
          <div class="mb-4 p-4 bg-gradient-to-r from-blue-100 to-emerald-100 border-2 border-blue-400 rounded-xl">
            <div class="flex items-center gap-2 mb-2">
              <svg class="w-5 h-5 text-blue-700" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span class="text-sm font-bold text-blue-900">🚀 Q1 2026 EARLY ADOPTER RATE</span>
            </div>
            <div class="flex items-baseline gap-3">
              <span class="text-3xl font-bold text-gray-400 line-through">$39</span>
              <span class="text-5xl font-bold bg-gradient-to-r from-blue-700 to-emerald-700 bg-clip-text text-transparent">$19</span>
              <span class="text-gray-700">/month</span>
            </div>
            <div class="mt-2 text-sm font-bold text-blue-900">
              🔒 Lifetime Lock • Trade with Institutional-Grade Data
            </div>
            <div class="mt-1 text-xs text-blue-800">
              ⏰ Public launch March 31, 2026 • Use code: <span class="font-mono font-bold bg-white px-2 py-0.5 rounded">FOUNDERS2026</span>
            </div>
          </div>
          
          <div v-if="billingCycle === 'yearly'" class="text-sm text-amber-700 font-semibold mb-3">
            Yearly: $399/year (save 15%)
          </div>
          <div class="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-lg text-xs font-bold">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <span>7-Day Free Trial • No Credit Card Required</span>
          </div>
        </div>

        <div class="border-t border-amber-200 pt-6 mb-8">
          <div class="mb-4 flex items-center gap-2 text-sm font-bold text-amber-900">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span>Everything in Free, plus AI Features:</span>
          </div>
          <ul class="space-y-4">
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <div>
                <div class="font-semibold text-gray-900">150 API Calls/Day</div>
                <div class="text-sm text-gray-600">Use in ChatGPT & Claude Desktop via MCP integration</div>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <div>
                <div class="font-semibold text-gray-900">RL Portfolio Optimization</div>
                <div class="text-sm text-gray-600">Deep reinforcement learning for smarter trades</div>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <div>
                <div class="font-semibold text-gray-900">AI Consensus Analysis</div>
                <div class="text-sm text-gray-600">Multi-engine agreement for confident decisions</div>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <div>
                <div class="font-semibold text-gray-900">Market Sentiment & Trends</div>
                <div class="text-sm text-gray-600">Real-time market mood tracking with FinBERT</div>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <div>
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-900">Predictive Analytics</span>
                  <router-link to="/transparency" class="text-xs text-purple-700 hover:text-purple-900 underline font-medium">61.18% validated</router-link>
                </div>
                <div class="text-sm text-gray-600">LSTM-powered stock predictions with proven accuracy</div>
              </div>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <div>
                <div class="font-semibold text-gray-900">AI Financial Assistant</div>
                <div class="text-sm text-gray-600">Unlimited intelligent chat for market insights</div>
              </div>
            </li>
          </ul>
        </div>

        <button 
          @click="upgradeToPremium"
          :disabled="isCreatingCheckout"
          class="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="!isCreatingCheckout">🔒 Start Free Trial - Lock in $19/mo</span>
          <span v-else>Creating checkout...</span>
          <svg v-if="!isCreatingCheckout" class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- API & MCP Integration Pricing -->
    <div class="max-w-6xl mx-auto mt-20 px-4">
      <div class="text-center mb-12">
        <div class="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
          </svg>
          <span>API & MCP Integration</span>
        </div>
        <h2 class="text-4xl font-bold text-gray-900 mb-4">Use InvestBuddy AI in ChatGPT & Claude</h2>
        <p class="text-lg text-gray-600 max-w-2xl mx-auto">
          Get AI-powered stock predictions directly in your favorite AI assistant. No app switching required.
        </p>
      </div>

      <div class="grid md:grid-cols-3 gap-6 mb-8">
        <!-- API Free Tier -->
        <div class="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
          <div class="mb-4">
            <h3 class="text-xl font-bold text-gray-900 mb-2">Free API Access</h3>
            <div class="flex items-baseline gap-2 mb-3">
              <span class="text-4xl font-bold text-gray-900">5</span>
              <span class="text-gray-600">calls/day</span>
            </div>
            <p class="text-sm text-gray-600">Perfect for trying it out</p>
          </div>
          <ul class="space-y-3 mb-6">
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>5 predictions per day</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>61.18% accuracy</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>ChatGPT & Claude</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>Resets daily at midnight</span>
            </li>
          </ul>
          <div class="text-center text-sm text-gray-500 font-medium">
            Included with Free Plan
          </div>
        </div>

        <!-- API Premium Tier -->
        <div class="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-xl border-2 border-purple-300 p-6 relative">
          <div class="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div class="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold">
              MOST POPULAR
            </div>
          </div>
          <div class="mb-4 pt-2">
            <h3 class="text-xl font-bold text-gray-900 mb-2">Premium API</h3>
            <div class="flex items-baseline gap-2 mb-3">
              <span class="text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">150</span>
              <span class="text-gray-600">calls/day</span>
            </div>
            <p class="text-sm text-gray-700 font-medium">For active traders</p>
          </div>
          <ul class="space-y-3 mb-6">
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span><strong>Full web platform access</strong></span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>150 API predictions per day</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>All 5 MCP tools</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>Portfolio analysis</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>Stock discovery AI</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>Batch predictions</span>
            </li>
          </ul>
          <div class="text-center">
            <button 
              v-if="!isAuthenticated"
              @click="$router.push('/login')"
              class="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all mb-2"
            >
              Get Premium Now
            </button>
            <button 
              v-else-if="!isPremium"
              @click="handleUpgrade('premium')"
              class="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all mb-2"
            >
              Get Premium Now
            </button>
            <div v-else class="px-6 py-3 bg-green-100 text-green-800 rounded-lg font-semibold mb-2">
              ✓ Current Plan
            </div>
            <div class="mt-2">
              <div class="text-sm text-gray-400 line-through">$39/mo</div>
              <div class="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">$19/mo with FOUNDERS2026</div>
              <div class="text-xs text-gray-500 mt-1">🔒 Locked for life • Public launch March 31</div>
            </div>
          </div>
        </div>

        <!-- API Unlimited Tier -->
        <div class="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
          <div class="mb-4">
            <h3 class="text-xl font-bold text-gray-900 mb-2">Unlimited API</h3>
            <div class="flex items-baseline gap-2 mb-3">
              <span class="text-4xl font-bold text-gray-900">∞</span>
              <span class="text-gray-600">calls/day</span>
            </div>
            <p class="text-sm text-gray-600">For algo-traders & funds</p>
            <div class="mt-2 text-sm font-semibold text-gray-700">$149/month</div>
          </div>
          <ul class="space-y-3 mb-6">
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>Unlimited predictions</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>All MCP tools</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>Priority support</span>
            </li>
            <li class="flex items-start gap-2 text-sm text-gray-700">
              <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              <span>Custom integrations</span>
            </li>
          </ul>
          <div class="text-center">
            <a href="mailto:support@investbuddy.ai?subject=Enterprise%20API%20Access%20Inquiry&body=Hi%20InvestBuddy%20team%2C%0A%0AI'm%20interested%20in%20Enterprise%20API%20access%20with%20unlimited%20predictions.%0A%0ACompany%3A%20%0AUse%20Case%3A%20%0AEstimated%20Monthly%20API%20Calls%3A%20%0A%0APlease%20contact%20me%20at%3A%0AName%3A%20%0AEmail%3A%20%0APhone%3A%20%0A%0AThank%20you!" class="inline-block px-6 py-2 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-colors text-sm">
              Request Access
            </a>
          </div>
        </div>
      </div>

      <div class="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border-2 border-purple-200">
        <div class="flex items-start gap-4">
          <div class="flex-shrink-0">
            <svg class="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="flex-1">
            <h4 class="font-bold text-gray-900 mb-2">How API Pricing Works</h4>
            <p class="text-sm text-gray-700 mb-3">
              Your API tier is automatically determined by your subscription plan. Free users get 30 API calls/day, Premium users get 150/day.
              When you upgrade to Premium, your API key immediately gets the higher limit—no configuration needed.
            </p>
            <div class="flex gap-4 text-sm">
              <router-link to="/mcp" class="text-purple-600 hover:text-purple-800 font-semibold underline">
                Setup Guide →
              </router-link>
              <router-link to="/settings/api-keys" class="text-purple-600 hover:text-purple-800 font-semibold underline">
                Get API Key →
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="max-w-3xl mx-auto mt-20 px-4">
      <h2 class="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
      <div class="space-y-4">
        <div class="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
          <h3 class="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <svg class="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
            </svg>
            How does the 7-day free trial work?
          </h3>
          <p class="text-gray-700">
            When you sign up for Premium, you get instant access to all AI features for 7 days completely free. 
            During this period, you can explore RL portfolio optimization, AI consensus analysis, market sentiment tracking, 
            and more. After the trial ends, your subscription begins, and you'll be charged based on your selected billing cycle.
          </p>
        </div>
        
        <div class="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
          <h3 class="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <svg class="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
            </svg>
            What happens after the trial period?
          </h3>
          <p class="text-gray-700">
            After your 7-day trial, you'll automatically be subscribed to the Premium plan at your chosen billing frequency 
            (monthly or yearly). If you decide Premium isn't for you, you can cancel anytime before the trial ends, 
            and you won't be charged. After cancellation, you'll be downgraded to the Free plan.
          </p>
        </div>

        <div class="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
          <h3 class="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <svg class="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
            </svg>
            How much can I save with yearly billing?
          </h3>
          <p class="text-gray-700">
            By choosing our yearly plan, you pay $399/year instead of $468/year (12 × $39), saving you $69 annually—that's 
            more than a month of Premium for free! The yearly plan gives you the same powerful AI features at a 15% discount.
          </p>
        </div>
        
        <div class="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
          <h3 class="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <svg class="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
            </svg>
            What AI technologies power InvestBuddy.ai Premium?
          </h3>
          <p class="text-gray-700">
            Our Premium features leverage state-of-the-art machine learning including Deep Reinforcement Learning (DRL) agents 
            trained on millions of market scenarios, FinBERT sentiment analysis for news processing, graph neural networks for 
            risk correlation modeling, and ensemble consensus systems that combine multiple AI engines for maximum accuracy.
          </p>
        </div>

        <div class="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
          <h3 class="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <svg class="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
            </svg>
            Can I switch between monthly and yearly plans?
          </h3>
          <p class="text-gray-700">
            Yes! You can switch your billing cycle at any time from your account settings. When upgrading from monthly to yearly, 
            you'll receive a prorated credit for your remaining monthly subscription. Switching from yearly to monthly will 
            take effect at the end of your current billing period.
          </p>
        </div>
      </div>
    </div>

    <!-- Alert Dialog -->
    <AlertDialog
      :is-open="showAlert"
      :type="alertType"
      :message="alertMessage"
      @close="showAlert = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { apiService } from '@/services/api'
import { analytics } from '@/services/analytics'
import AlertDialog from '@/components/dialogs/AlertDialog.vue'

const router = useRouter()
const authStore = useAuthStore()
const billingCycle = ref<'monthly' | 'yearly'>('monthly')

// Alert dialog state
const showAlert = ref(false)
const alertType = ref<'success' | 'error' | 'warning' | 'info'>('info')
const alertMessage = ref('')

// Loading state for checkout
const isCreatingCheckout = ref(false)

// Check if user is already a paid premium user
const isPaidUser = computed(() => {
  return authStore.user?.subscriptionPlan === 'premium' && 
         authStore.user?.subscriptionStatus === 'active'
})

const isAdmin = computed(() => authStore.user?.isAdmin || false)

// Real trial calculation from user data
const isInTrial = computed(() => {
  const user = authStore.user
  return user?.subscriptionStatus === 'trial'
})

const trialDaysRemaining = computed(() => {
  const user = authStore.user
  if (!user || !user.trialExpiresAt) return 0
  
  const now = new Date()
  const expiresAt = new Date(user.trialExpiresAt)
  const diffMs = expiresAt.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  
  return Math.max(0, diffDays)
})

function toggleBillingCycle() {
  billingCycle.value = billingCycle.value === 'monthly' ? 'yearly' : 'monthly'
}

async function upgradeToPremium() {
  if (isCreatingCheckout.value) return

  try {
    isCreatingCheckout.value = true

    // Check if user is authenticated
    if (!authStore.isAuthenticated || !authStore.user) {
      // Track failed attempt (user not logged in)
      analytics.trackEvent({
        category: 'Subscription',
        action: 'premium_upgrade_failed',
        label: 'not_authenticated',
        customData: {
          status: 'failed',
          reason: 'not_authenticated',
          billing_cycle: billingCycle.value
        }
      })
      
      alertType.value = 'error'
      alertMessage.value = 'Please log in to upgrade to Premium'
      showAlert.value = true
      router.push('/login')
      return
    }

    // Track premium button click with user details
    analytics.trackEvent({
      category: 'Subscription',
      action: 'premium_upgrade_clicked',
      label: billingCycle.value,
      customData: {
        status: 'initiated',
        user_id: authStore.user.id,
        user_email: authStore.user.email,
        billing_cycle: billingCycle.value,
        has_trial: !!authStore.user.trialExpiresAt,
        is_admin: authStore.user.isAdmin || false
      }
    })

    // Create Lemon Squeezy checkout session
    // The token will be sent automatically via axios interceptor in apiService
    const response = await apiService.createCheckout(billingCycle.value)

    const { checkoutUrl } = response

    // Track successful checkout creation
    analytics.trackEvent({
      category: 'Subscription',
      action: 'premium_checkout_created',
      label: billingCycle.value,
      customData: {
        user_id: authStore.user.id,
        billing_cycle: billingCycle.value,
        checkout_url: checkoutUrl
      }
    })

    // Redirect to Lemon Squeezy checkout
    window.location.href = checkoutUrl
  } catch (error: any) {
    console.error('❌ Error creating checkout:', error)
    
    // Track checkout failure
    analytics.trackEvent({
      category: 'Subscription',
      action: 'premium_checkout_failed',
      label: error.response?.status === 403 ? 'forbidden' : 'error',
      customData: {
        status: 'failed',
        reason: error.response?.status === 403 ? 'forbidden' : 'error',
        error_message: error.response?.data?.error || error.message,
        user_id: authStore.user?.id,
        billing_cycle: billingCycle.value
      }
    })
    
    alertType.value = 'error'
    alertMessage.value = error.response?.data?.error || 'Failed to create checkout. Please try again.'
    showAlert.value = true
  } finally {
    isCreatingCheckout.value = false
  }
}
</script>
